import { DEFAULT_DEBOUNCE_TIME } from "@/config";
import { useDebounce } from "@/hooks";
import {
  milestoneTemplateType,
  projectMilestoneTemplateType,
} from "@/propTypes";
import { useSearchMilestoneTemplatesByPropertiesQuery } from "@/services";
import { Input } from "antd";
import { arrayOf, func, oneOfType, shape, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import * as S from "../styled";
import { duplicateMilestoneTemplateValueExists } from "./helper";

const MilestoneTemplateInput = ({
  milestoneTemplate,
  setMilestoneTemplate,
  property,
  setIsDuplicateForValidationCheck,
  comparisonMilestones,
}) => {
  const intl = useIntl();

  const [isDuplicate, setIsDuplicate] = useState(false);
  const [value, setValue] = useState();
  const [queryParameters, setQueryParameters] = useState();

  const debouncedValue = useDebounce(value, DEFAULT_DEBOUNCE_TIME);

  const { data: milestoneTemplates, isError } =
    useSearchMilestoneTemplatesByPropertiesQuery(queryParameters, {
      skip: !queryParameters,
    });

  useEffect(() => {
    if (!isError) return;

    toast.error(intl.formatMessage({ id: "milestoneTemplates.searchError" }));
  }, [intl, isError]);

  useEffect(() => {
    if (!milestoneTemplates) return;

    const duplicateExists = duplicateMilestoneTemplateValueExists(
      milestoneTemplate?.id,
      milestoneTemplates,
      property,
      value
    );

    setIsDuplicate(duplicateExists);
  }, [milestoneTemplate?.id, milestoneTemplates, property, value]);

  useEffect(() => {
    if (!value) {
      setIsDuplicate(false);
      return;
    }

    if (debouncedValue !== value) return;

    //Existing Milestone Templates
    if (comparisonMilestones && comparisonMilestones.length > 0) {
      const duplicateToComparisonExists = duplicateMilestoneTemplateValueExists(
        milestoneTemplate?.id,
        comparisonMilestones,
        property,
        value
      );

      setIsDuplicate(duplicateToComparisonExists);
    } else {
      setQueryParameters({
        property: property,
        value: value,
      });
    }
  }, [
    debouncedValue,
    intl,
    milestoneTemplate?.id,
    property,
    value,
    comparisonMilestones,
  ]);

  useEffect(() => {
    setValue(milestoneTemplate?.[property]);
  }, [milestoneTemplate, property]);

  useEffect(() => {
    setIsDuplicateForValidationCheck(isDuplicate);
  }, [isDuplicate, setIsDuplicateForValidationCheck]);

  const isFieldInvalid = (value) => value.startsWith(" ");

  const onChange = (value) => {
    if (isFieldInvalid(value)) return;

    setMilestoneTemplate({ ...milestoneTemplate, [property]: value });
  };

  return (
    <>
      <h4
        data-test-id={`name-and-abbreviation-editor-${property.toLowerCase()}-label`}
      >
        {intl.formatMessage({
          id: `milestoneTemplates.${property.toLowerCase()}Label`,
        })}
      </h4>
      <Input
        data-test-id={`name-and-abbreviation-editor-${property.toLowerCase()}-input`}
        value={value}
        onChange={({ target: { value } }) => onChange(value)}
        disabled={!!milestoneTemplate?.id}
      />
      {isDuplicate && (
        <S.StyledTag
          data-test-id={`name-and-abbreviation-editor-dup-${property.toLowerCase()}-error-label`}
        >
          {intl.formatMessage({ id: `milestoneTemplates.dup${property}Error` })}
        </S.StyledTag>
      )}
    </>
  );
};

MilestoneTemplateInput.propTypes = {
  milestoneTemplate: oneOfType([
    milestoneTemplateType,
    projectMilestoneTemplateType,
  ]),
  setMilestoneTemplate: func.isRequired,
  property: string.isRequired,
  setIsDuplicateForValidationCheck: func.isRequired,
  comparisonMilestones: arrayOf(
    shape({
      id: string,
      Name: string,
      Abbreviation: string,
    })
  ),
};

export default MilestoneTemplateInput;
